import React, { useEffect, useState, useCallback } from "react";
import { db } from "../utils/firebase";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import {
  LuX, LuUser, LuBuilding2, LuMapPin, LuFileText,
  LuExternalLink, LuChevronRight, LuCheck, LuCircleX, LuClock,
} from "react-icons/lu";
import { toast } from "react-toastify";

const PAGE_SIZE = 20;
const inputCls  = "border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:border-ring transition-colors bg-card";

/* ── helpers ─────────────────────────────────────────────────── */
function getInitials(name = "") {
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?";
}

function StatusBadge({ status }) {
  const cfg = {
    approved: { cls: "bg-green-100 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900/50", label: "Approved", Icon: LuCheck },
    rejected: { cls: "bg-red-100 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900/50",   label: "Rejected", Icon: LuCircleX },
    pending:  { cls: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900/50", label: "Pending",  Icon: LuClock },
  };
  const { cls, label, Icon } = cfg[status] || cfg.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${cls}`}>
      <Icon size={9} strokeWidth={3} />
      {label}
    </span>
  );
}

function DetailField({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-black text-[var(--color-muted-foreground)] uppercase tracking-widest">
        {label}
      </span>
      <span className="text-sm text-foreground font-medium break-words">{value}</span>
    </div>
  );
}

function DetailSection({ icon, title, children }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 space-y-4">
      <div className="flex items-center gap-2 pb-1 border-b border-border">
        <span className="text-brand-amber">{icon}</span>
        <h3 className="text-xs font-black text-foreground uppercase tracking-widest">{title}</h3>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">{children}</div>
    </div>
  );
}

/* ── detail panel ────────────────────────────────────────────── */
function UserDetailPanel({ user, onClose, onApprove, onReject }) {
  const [actionLoading, setActionLoading] = useState(false);
  const isImage = user.permitUrl && /\.(jpg|jpeg|png|gif|webp)/i.test(user.permitUrl.split("?")[0]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await onApprove(user.id);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      await onReject(user.id);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`User details for ${user.name}`}
        className="fixed top-0 right-0 h-full w-full sm:max-w-lg bg-background z-50 shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="bg-brand-navy px-6 py-5 flex items-center gap-4 flex-shrink-0">
          <div className="w-12 h-12 rounded-full bg-brand-amber/20 border-2 border-brand-amber/40 flex items-center justify-center flex-shrink-0">
            <span className="text-brand-amber font-black text-lg leading-none">{getInitials(user.name)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-white font-black text-lg leading-tight truncate">{user.name || "—"}</h2>
              <StatusBadge status={user.status} />
            </div>
            <p className="text-slate-400 text-xs truncate mt-0.5">{user.email || "—"}</p>
          </div>
          <button onClick={onClose} aria-label="Close panel" className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10 flex-shrink-0 cursor-pointer">
            <LuX size={20} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-background">

          {/* Approval actions */}
          {user.status !== "approved" && (
            <div className="bg-card rounded-xl border border-border p-4 flex gap-3">
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors cursor-pointer"
              >
                <LuCheck size={14} strokeWidth={3} />
                Approve
              </button>
              {user.status !== "rejected" && (
                <button
                  onClick={handleReject}
                  disabled={actionLoading}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors cursor-pointer"
                >
                  <LuCircleX size={14} />
                  Decline
                </button>
              )}
            </div>
          )}
          {user.status === "approved" && (
            <div className="bg-card rounded-xl border border-border p-4 flex gap-3">
              <button
                onClick={handleReject}
                disabled={actionLoading}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold border border-red-300 dark:border-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <LuCircleX size={14} />
                Revoke Access
              </button>
            </div>
          )}

          {/* Personal Information */}
          <DetailSection icon={<LuUser size={14} />} title="Personal Information">
            <DetailField label="Full Name"     value={user.name} />
            <DetailField label="Username"      value={user.username ? `@${user.username}` : null} />
            <DetailField label="Email"         value={user.email} />
            <DetailField label="Phone"         value={user.phone} />
            <DetailField label="Date of Birth" value={user.dob} />
            <DetailField label="Gender"        value={user.gender} />
          </DetailSection>

          {/* Business Information */}
          <DetailSection icon={<LuBuilding2 size={14} />} title="Business Information">
            <DetailField label="Business Name (DBA)" value={user.businessName} />
            <DetailField label="Tax ID"               value={user.taxId} />
          </DetailSection>

          {/* Shipping Address */}
          <DetailSection icon={<LuMapPin size={14} />} title="Shipping Address">
            <div className="col-span-2">
              <DetailField label="Street Address" value={user.address} />
            </div>
            <DetailField label="City"     value={user.city} />
            <DetailField label="State"    value={user.state} />
            <DetailField label="ZIP Code" value={user.zip} />
          </DetailSection>

          {/* Tax Permit / Business License */}
          {user.permitUrl && (
            <div className="bg-card rounded-xl border border-border p-5 space-y-4">
              <div className="flex items-center gap-2 pb-1 border-b border-border">
                <span className="text-brand-amber"><LuFileText size={14} /></span>
                <h3 className="text-xs font-black text-foreground uppercase tracking-widest">
                  Tax Permit / Business License
                </h3>
              </div>
              {isImage ? (
                <div className="space-y-3">
                  <img src={user.permitUrl} alt="Business permit" className="w-full max-h-52 object-contain rounded-lg border border-border bg-muted" />
                  <a href={user.permitUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-amber hover:underline">
                    <LuExternalLink size={12} /> Open Full Size
                  </a>
                </div>
              ) : (
                <a href={user.permitUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-amber/10 border border-brand-amber/25 text-brand-amber text-sm font-bold rounded-xl hover:bg-brand-amber/20 transition-colors">
                  <LuExternalLink size={14} /> View Document
                </a>
              )}
            </div>
          )}

          {/* UID */}
          <div className="rounded-xl border border-border bg-muted/40 px-4 py-3">
            <p className="text-[10px] font-black text-[var(--color-muted-foreground)] uppercase tracking-widest mb-1">Firebase UID</p>
            <p className="text-xs font-mono text-[var(--color-muted-foreground)] break-all">{user.uid || user.id}</p>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── main table ──────────────────────────────────────────────── */
function UsersTable() {
  const [users,        setUsers]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [filterName,   setFilterName]   = useState("");
  const [filterCity,   setFilterCity]   = useState("");
  const [filterState,  setFilterState]  = useState("");
  const [filterStatus, setFilterStatus] = useState("pending"); // default to pending for admin workflow
  const [page,         setPage]         = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "users"));
        setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => setPage(1), [filterName, filterCity, filterState, filterStatus]);

  const closePanel = useCallback(() => setSelectedUser(null), []);

  const handleApprove = async (userId) => {
    try {
      await updateDoc(doc(db, "users", userId), { status: "approved" });
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, status: "approved" } : u));
      setSelectedUser((prev) => prev?.id === userId ? { ...prev, status: "approved" } : prev);
      toast.success("Account approved.");
    } catch (err) {
      console.error("Approve failed:", err);
      toast.error(
        err.code === "permission-denied"
          ? "Permission denied — check Firestore rules for admin write access to users collection."
          : "Failed to approve account. Please try again."
      );
    }
  };

  const handleReject = async (userId) => {
    try {
      await updateDoc(doc(db, "users", userId), { status: "rejected" });
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, status: "rejected" } : u));
      setSelectedUser((prev) => prev?.id === userId ? { ...prev, status: "rejected" } : prev);
      toast.success("Account declined.");
    } catch (err) {
      console.error("Reject failed:", err);
      toast.error(
        err.code === "permission-denied"
          ? "Permission denied — check Firestore rules for admin write access to users collection."
          : "Failed to decline account. Please try again."
      );
    }
  };

  const filtered = users
    .filter((u) => filterName   ? u.name?.toLowerCase().includes(filterName.toLowerCase())   : true)
    .filter((u) => filterCity   ? u.city?.toLowerCase().includes(filterCity.toLowerCase())   : true)
    .filter((u) => filterState  ? u.state?.toLowerCase().includes(filterState.toLowerCase()) : true)
    .filter((u) => filterStatus ? (u.status || "pending") === filterStatus                   : true);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Pending count for the filter label
  const pendingCount = users.filter((u) => !u.status || u.status === "pending").length;

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="animate-pulse bg-muted rounded-2xl h-16" />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <input type="text" placeholder="Search by name…"  value={filterName}  onChange={(e) => setFilterName(e.target.value)}  className={`${inputCls} w-full sm:w-auto`} />
          <input type="text" placeholder="Filter by city…"  value={filterCity}  onChange={(e) => setFilterCity(e.target.value)}  className={`${inputCls} w-full sm:w-auto`} />
          <input type="text" placeholder="Filter by state…" value={filterState} onChange={(e) => setFilterState(e.target.value)} className={`${inputCls} w-full sm:w-auto`} />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={`${inputCls} w-full sm:w-auto`}>
            <option value="">All Status</option>
            <option value="pending">Pending {pendingCount > 0 ? `(${pendingCount})` : ""}</option>
            <option value="approved">Approved</option>
            <option value="rejected">Declined</option>
          </select>
          <span className="text-xs text-[var(--color-muted-foreground)] self-center font-medium sm:ml-auto">
            {filtered.length} user{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* ── Mobile card list (< md) ───────────────────────────── */}
        {paginated.length === 0 ? (
          <div className="md:hidden bg-card rounded-2xl border border-border p-12 text-center text-[var(--color-muted-foreground)] text-sm">
            No users found.
          </div>
        ) : (
          <div className="md:hidden space-y-3">
            {paginated.map((user) => (
              <div
                key={user.id}
                onClick={() => setSelectedUser(user)}
                className="bg-card border border-border rounded-2xl px-4 py-3.5 flex items-center gap-3 cursor-pointer hover:bg-muted active:bg-muted transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-brand-amber/10 border border-brand-amber/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-amber font-black text-sm leading-none">{getInitials(user.name)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-sm text-foreground truncate">{user.name || "—"}</p>
                    <StatusBadge status={user.status} />
                  </div>
                  <p className="text-xs text-[var(--color-muted-foreground)] truncate">{user.email}</p>
                  {(user.businessName || user.city) && (
                    <p className="text-xs text-[var(--color-muted-foreground)] truncate mt-0.5">
                      {[user.businessName, user.city && user.state ? `${user.city}, ${user.state}` : user.city].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
                <LuChevronRight size={16} className="text-brand-amber flex-shrink-0" />
              </div>
            ))}
          </div>
        )}

        {/* ── Desktop table (≥ md) ──────────────────────────────── */}
        <div className="hidden md:block bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          {paginated.length === 0 ? (
            <div className="p-12 text-center text-[var(--color-muted-foreground)] text-sm">No users found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-brand-navy text-white">
                    {["Name", "Email", "Username", "Business", "Phone", "City", "State", "Status", ""].map((h) => (
                      <th key={h} className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {paginated.map((user) => (
                    <tr key={user.id} onClick={() => setSelectedUser(user)} className="hover:bg-muted transition-colors cursor-pointer group">
                      <td className="px-4 py-3 font-semibold text-foreground whitespace-nowrap">{user.name}</td>
                      <td className="px-4 py-3 text-[var(--color-muted-foreground)] whitespace-nowrap">{user.email}</td>
                      <td className="px-4 py-3 text-[var(--color-muted-foreground)] font-mono text-xs">{user.username}</td>
                      <td className="px-4 py-3 text-[var(--color-muted-foreground)] whitespace-nowrap">
                        {user.businessName || <span className="italic text-xs opacity-40">—</span>}
                      </td>
                      <td className="px-4 py-3 text-[var(--color-muted-foreground)] whitespace-nowrap">{user.phone}</td>
                      <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{user.city}</td>
                      <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{user.state}</td>
                      <td className="px-4 py-3"><StatusBadge status={user.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center gap-0.5 text-xs font-bold text-brand-amber group-hover:underline">
                          View <LuChevronRight size={13} />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-[var(--color-muted-foreground)]">
              Page {page} of {totalPages} · {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} users
            </p>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 text-xs font-bold rounded-xl border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ← Prev
              </button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 text-xs font-bold rounded-xl border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedUser && (
        <UserDetailPanel
          user={selectedUser}
          onClose={closePanel}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}
    </>
  );
}

export default UsersTable;
